import { type AdminAgentArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    artifactRegistryKey,
    isRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

export type ArtifactDownloadSource =
    | {
          readonly kind: 'text';
          readonly content: string;
          readonly contentType: string;
      }
    | {
          readonly kind: 'base64';
          readonly data: string;
          readonly contentType: string;
      }
    | {
          readonly kind: 'href';
          readonly href: string;
      };

export type ArtifactDownload = {
    readonly filename: string;
    readonly source: ArtifactDownloadSource;
};

type ArtifactDownloadResolver = (
    artifact: AdminAgentArtifact,
    apiUrl: string,
) => ArtifactDownload | null;

const DEFAULT_JSON_MIME = 'application/json';

// Extensions default to `.json` because every artifact payload is durable JSON
// unless the type has a more natural on-disk representation. Overrides live
// alongside their specialized resolver below.
const DEFAULT_EXTENSION_BY_ARTIFACT_KEY: Record<string, string> = {
    // keep-sorted start
    'admin.chart@1': 'json',
    'admin.code_sandbox.diff@1': 'json',
    'admin.code_sandbox.output@1': 'json',
    'admin.csv@1': 'csv',
    'admin.file.binary@1': 'bin',
    'admin.file.csv@1': 'csv',
    'admin.file.html@1': 'html',
    'admin.file.jpeg@1': 'jpg',
    'admin.file.json@1': 'json',
    'admin.file.markdown@1': 'md',
    'admin.file.pdf@1': 'pdf',
    'admin.file.png@1': 'png',
    'admin.file.text@1': 'txt',
    'admin.file.xlsx@1': 'xlsx',
    'admin.file.zip@1': 'zip',
    'admin.image@1': 'png',
    'admin.python_snippet.output@1': 'json',
    'admin.run_data_diff.output@1': 'json',
    'admin.user_uploaded_image@1': 'png',
    // keep-sorted end
};

const SPECIALIZED_RESOLVERS: Record<string, ArtifactDownloadResolver> = {
    // keep-sorted start block=yes
    'admin.csv@1': (artifact) => fileContentDownload(artifact),
    'admin.file.binary@1': (artifact) => fileContentDownload(artifact),
    'admin.file.csv@1': (artifact) => fileContentDownload(artifact),
    'admin.file.html@1': (artifact) => fileContentDownload(artifact),
    'admin.file.jpeg@1': (artifact) => fileContentDownload(artifact),
    'admin.file.json@1': (artifact) => fileContentDownload(artifact),
    'admin.file.markdown@1': (artifact) => fileContentDownload(artifact),
    'admin.file.pdf@1': (artifact) => fileContentDownload(artifact),
    'admin.file.png@1': (artifact) => fileContentDownload(artifact),
    'admin.file.text@1': (artifact) => fileContentDownload(artifact),
    'admin.file.xlsx@1': (artifact) => fileContentDownload(artifact),
    'admin.file.zip@1': (artifact) => fileContentDownload(artifact),
    'admin.image@1': (artifact, apiUrl) => imageHrefDownload(artifact, apiUrl),
    'admin.user_uploaded_image@1': (artifact, apiUrl) =>
        imageHrefDownload(artifact, apiUrl),
    // keep-sorted end
};

export function getArtifactDownload(
    artifact: AdminAgentArtifact,
    apiUrl: string,
): ArtifactDownload | null {
    const registryKey = artifactRegistryKey(artifact);
    const specialized = SPECIALIZED_RESOLVERS[registryKey];
    if (specialized) {
        const resolved = specialized(artifact, apiUrl);
        if (resolved) {
            return resolved;
        }
    }
    return jsonPayloadDownload(artifact);
}

// Public fallback used by the button when a specialized source (e.g. an
// invalid base64 payload) fails to build a Blob at runtime, so the download
// affordance never silently disappears.
export function buildJsonPayloadDownload(
    artifact: AdminAgentArtifact,
): ArtifactDownload | null {
    return jsonPayloadDownload(artifact);
}

function jsonPayloadDownload(
    artifact: AdminAgentArtifact,
): ArtifactDownload | null {
    let serialized: string;
    try {
        serialized = JSON.stringify(artifact.payload, null, 2) ?? '';
    } catch {
        return null;
    }
    if (serialized.length === 0) {
        return null;
    }
    // JSON fallback always uses `.json` regardless of the artifact type's
    // preferred extension — an admin.file.binary payload dump is JSON, not
    // an opaque binary, and the filename must reflect that.
    return {
        filename: artifactFilename(artifact, 'json'),
        source: {
            kind: 'text',
            content: serialized,
            contentType: DEFAULT_JSON_MIME,
        },
    };
}

function fileContentDownload(
    artifact: AdminAgentArtifact,
): ArtifactDownload | null {
    if (!isRecord(artifact.payload)) {
        return null;
    }
    const contentType = readTrimmedString(artifact.payload, 'content_type');
    if (contentType === null) {
        return null;
    }
    const providedFilename = readTrimmedString(artifact.payload, 'filename');
    const filename =
        providedFilename ??
        artifactFilename(artifact, extensionForArtifact(artifact, 'bin'));

    const base64Data = readTrimmedString(artifact.payload, 'base64_data');
    if (base64Data !== null) {
        return {
            filename,
            source: { kind: 'base64', data: base64Data, contentType },
        };
    }
    const textContent = readRawString(artifact.payload, 'content');
    if (textContent !== null) {
        return {
            filename,
            source: { kind: 'text', content: textContent, contentType },
        };
    }
    return null;
}

function imageHrefDownload(
    artifact: AdminAgentArtifact,
    apiUrl: string,
): ArtifactDownload {
    const payloadFilename = isRecord(artifact.payload)
        ? readTrimmedString(artifact.payload, 'filename')
        : null;
    const mimeType = isRecord(artifact.payload)
        ? readTrimmedString(artifact.payload, 'mime_type')
        : null;
    const extension =
        extensionFromMimeType(mimeType) ??
        extensionForArtifact(artifact, 'png');
    const filename = payloadFilename ?? artifactFilename(artifact, extension);
    const base = `${apiUrl}/admin/agent/runs/${artifact.phoebe_agent_run_id}/artifacts/${artifact.id}/image`;
    // ?download=1 makes the server emit `Content-Disposition: attachment`
    // so a save prompt fires even when the API and web app are on
    // different origins (production). The HTML `download` attribute alone
    // is silently ignored by browsers on cross-origin responses.
    const query = new URLSearchParams({ download: '1', filename }).toString();
    return {
        filename,
        source: { kind: 'href', href: `${base}?${query}` },
    };
}

function extensionForArtifact(
    artifact: AdminAgentArtifact,
    fallback: string,
): string {
    return (
        DEFAULT_EXTENSION_BY_ARTIFACT_KEY[artifactRegistryKey(artifact)] ??
        fallback
    );
}

function extensionFromMimeType(mimeType: string | null): string | null {
    if (mimeType === null) {
        return null;
    }
    const normalized = mimeType.split(';', 1)[0]?.trim().toLowerCase() ?? '';
    switch (normalized) {
        case 'image/gif':
            return 'gif';
        case 'image/jpeg':
        case 'image/jpg':
            return 'jpg';
        case 'image/png':
            return 'png';
        case 'image/webp':
            return 'webp';
        default:
            return null;
    }
}

function readTrimmedString(
    payload: Record<string, unknown>,
    key: string,
): string | null {
    const value = payload[key];
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

// File payload `content` (CSV rows, markdown source, etc.) preserves trailing
// whitespace and newlines exactly as the tool emitted them.
function readRawString(
    payload: Record<string, unknown>,
    key: string,
): string | null {
    const value = payload[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

export function artifactFilename(
    artifact: AdminAgentArtifact,
    extension: string,
): string {
    const slug = slugifyFilenameStem(artifact.title || 'artifact');
    const stem = slug.length > 0 ? slug : 'artifact';
    return extension.length > 0 ? `${stem}.${extension}` : stem;
}

// Kebab-cased ASCII, at most 80 characters, always non-empty when the input
// has any word character. Matches other admin-agent filename helpers on tone
// but does not depend on them so the artifact registry stays self-contained.
export function slugifyFilenameStem(value: string): string {
    return value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
        .replace(/^-+|-+$/g, '');
}
